import api from './api'
import type { ApiResponse } from '@/types'

export type AdminFinancePeriod = 'month' | 'quarter' | 'year'
export interface FinancialComparison { current: number; previous: number; delta_pct: number | null }
export interface AdminFinanceDashboardData {
  currency: string
  currencies: string[]
  as_of: string
  period: { key: AdminFinancePeriod; from: string; to: string; previous_from: string; previous_to: string }
  capabilities: { collections: boolean; expenses: boolean; invoices: boolean; quotes: boolean }
  collected: FinancialComparison | null
  expenses: FinancialComparison | null
  net_cash_flow: FinancialComparison | null
  receivables: {
    outstanding: number; overdue: number; due_7: number; due_30: number
    buckets: { not_due: number; d1_30: number; d31_60: number; d61_plus: number; no_due_date: number }
    invoices: { id: string; number: string; due_date: string | null; balance: number; days_overdue: number }[]
  } | null
  series: { date: string; collected: number | null; expenses: number | null }[]
  teams: { id: number | null; name: string | null; collected: number | null; accepted_quotes: number | null }[]
  transactions: { id: string; kind: 'payment' | 'refund' | 'expense' | 'correction'; date: string; reference: string | null; amount: number; href: string | null }[]
}

export interface PaymentMixMethod { method: string; total: number; payment_count: number }

/** A payment reversed on the selected day — `paid_on` is when it originally arrived, usually an earlier day. */
export interface PaymentMixRefund { id: string; reference: string | null; method: string; amount: number; paid_on: string; href: string }

export interface PaymentMixDay {
  /** Echoed back by the server, which resolves the default on the clinic clock. */
  date: string
  currency: string
  /** Methods are GROSS for the day; `refunded` comes off once, at the total. */
  gross: number
  refunded: number
  net: number
  methods: PaymentMixMethod[]
  refunds: PaymentMixRefund[]
}

export async function getPaymentMix(date?: string, currency?: string): Promise<PaymentMixDay> {
  const response = await api.get<ApiResponse<PaymentMixDay>>('/dashboard/payment-mix', { params: { date, currency } })
  return response.data.data
}

export async function getAdminFinanceDashboard(period: AdminFinancePeriod, currency?: string): Promise<AdminFinanceDashboardData> {
  const response = await api.get<ApiResponse<AdminFinanceDashboardData>>('/dashboard/admin-finance', { params: { period, currency } })
  return response.data.data
}
